import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const InspectionDetailForm = ({ checkpoint, onSave, onApprove, onReject }) => {
  const [formData, setFormData] = useState({
    visualInspection: false,
    dimensionalCheck: false,
    functionalTest: false,
    materialVerification: false,
    surfaceQuality: false,
    assemblyIntegrity: false,
    notes: '',
    defectType: '',
    defectSeverity: '',
    correctiveAction: '',
    photos: []
  });

  const [isSaving, setIsSaving] = useState(false);

  const defectTypeOptions = [
    { value: '', label: 'Select defect type' },
    { value: 'dimensional', label: 'Dimensional Variance' },
    { value: 'surface', label: 'Surface Defect' },
    { value: 'material', label: 'Material Issue' },
    { value: 'assembly', label: 'Assembly Error' },
    { value: 'functional', label: 'Functional Failure' },
    { value: 'cosmetic', label: 'Cosmetic Damage' }
  ];

  const severityOptions = [
    { value: '', label: 'Select severity' },
    { value: 'critical', label: 'Critical - Stop Production' },
    { value: 'major', label: 'Major - Requires Rework' },
    { value: 'minor', label: 'Minor - Document Only' }
  ];

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev?.[field] }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    onSave(formData);
    setIsSaving(false);
  };

  const handleApprove = async () => {
    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    onApprove(formData);
    setIsSaving(false);
  };

  const handleReject = async () => {
    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    onReject(formData);
    setIsSaving(false);
  };

  const allChecksPassed = formData?.visualInspection && 
                          formData?.dimensionalCheck && 
                          formData?.functionalTest && 
                          formData?.materialVerification && 
                          formData?.surfaceQuality && 
                          formData?.assemblyIntegrity;

  if (!checkpoint) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-muted/30 rounded-xl">
        <Icon name="ClipboardCheck" size={64} color="var(--color-muted-foreground)" />
        <p className="mt-4 text-muted-foreground">Select a checkpoint to begin inspection</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-card rounded-xl border border-border">
      <div className="px-4 md:px-6 py-4 md:py-6 border-b border-border">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">
              {checkpoint?.productName}
            </h2>
            <p className="caption text-muted-foreground">
              Batch #{checkpoint?.batchNumber} • {checkpoint?.checkpointType}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-3 py-1 rounded-lg bg-primary/10 text-primary text-xs font-medium">
              {checkpoint?.priority?.toUpperCase()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          <div className="flex items-center gap-2">
            <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
            <span className="caption text-muted-foreground">{checkpoint?.location}</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="User" size={16} color="var(--color-muted-foreground)" />
            <span className="caption text-muted-foreground">{checkpoint?.assignedInspector}</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
            <span className="caption text-muted-foreground">
              Due: {new Date(checkpoint.dueDate)?.toLocaleString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-base font-semibold text-foreground mb-4">Quality Criteria Checklist</h3>
            <div className="space-y-3">
              <Checkbox
                label="Visual Inspection"
                description="Check for visible defects, damage, or irregularities"
                checked={formData?.visualInspection}
                onChange={() => handleCheckboxChange('visualInspection')}
              />
              <Checkbox
                label="Dimensional Check"
                description="Verify measurements meet specifications"
                checked={formData?.dimensionalCheck}
                onChange={() => handleCheckboxChange('dimensionalCheck')}
              />
              <Checkbox
                label="Functional Test"
                description="Test all functional components and mechanisms"
                checked={formData?.functionalTest}
                onChange={() => handleCheckboxChange('functionalTest')}
              />
              <Checkbox
                label="Material Verification"
                description="Confirm correct materials and finishes used"
                checked={formData?.materialVerification}
                onChange={() => handleCheckboxChange('materialVerification')}
              />
              <Checkbox
                label="Surface Quality"
                description="Inspect surface finish and coating quality"
                checked={formData?.surfaceQuality}
                onChange={() => handleCheckboxChange('surfaceQuality')}
              />
              <Checkbox
                label="Assembly Integrity"
                description="Verify proper assembly and fastening"
                checked={formData?.assemblyIntegrity}
                onChange={() => handleCheckboxChange('assemblyIntegrity')}
              />
            </div>
          </div>

          {!allChecksPassed && (
            <div className="space-y-4 p-4 rounded-xl bg-warning/10 border border-warning/20">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                Defect Documentation
              </h3>

              <Select
                label="Defect Type"
                options={defectTypeOptions}
                value={formData?.defectType}
                onChange={(value) => handleInputChange('defectType', value)}
                required
              />

              <Select
                label="Severity Level"
                options={severityOptions}
                value={formData?.defectSeverity}
                onChange={(value) => handleInputChange('defectSeverity', value)}
                required
              />

              <Input
                label="Corrective Action Required"
                type="text"
                placeholder="Describe required corrective action"
                value={formData?.correctiveAction}
                onChange={(e) => handleInputChange('correctiveAction', e?.target?.value)}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Inspector Notes
            </label>
            <textarea
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              rows="4"
              placeholder="Add detailed inspection notes, observations, or recommendations..."
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Photo Documentation
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {[1, 2, 3]?.map((index) => (
                <button
                  key={index}
                  className="aspect-square rounded-xl border-2 border-dashed border-border bg-muted/30 hover:bg-muted/50 transition-colors duration-250 flex flex-col items-center justify-center gap-2"
                >
                  <Icon name="Camera" size={24} color="var(--color-muted-foreground)" />
                  <span className="caption text-muted-foreground">Add Photo</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 md:px-6 py-4 md:py-6 border-t border-border">
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            fullWidth
            iconName="Save"
            onClick={handleSave}
            loading={isSaving}
          >
            Save Progress
          </Button>
          <Button
            variant="destructive"
            fullWidth
            iconName="XCircle"
            onClick={handleReject}
            loading={isSaving}
            disabled={allChecksPassed}
          >
            Reject
          </Button>
          <Button
            variant="success"
            fullWidth
            iconName="CheckCircle2"
            onClick={handleApprove}
            loading={isSaving}
            disabled={!allChecksPassed}
          >
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InspectionDetailForm;